import { useEffect } from 'react';
import { NotificationRow } from './NotificationRow';
import { useNotificationCoordinator, useNotificationInbox } from '../../../coordinators/notifications';
import { useNotificationListInteraction } from './hooks/useNotificationListInteraction';

interface NotificationsPageProps {
  onBack: () => void;
}

/**
 * Inbox page fed by the host API through `NotificationCoordinator`. Mark-read
 * runs when an unread row is dismissed (not on expand); the coordinator owns
 * the snapshot and unread state.
 */
export const NotificationsPage = ({ onBack }: NotificationsPageProps) => {
  const coordinator = useNotificationCoordinator();
  const { items, nextCursor, loading } = useNotificationInbox();
  const { expandedId, handleRowClick, showUnreadDot } = useNotificationListInteraction((id) =>
    void coordinator.markRead(id),
  );

  useEffect(() => {
    void coordinator.loadInbox();
  }, [coordinator]);

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <button
        type='button'
        onClick={onBack}
        className='mb-3 shrink-0 self-start border-none bg-transparent p-0 text-sm font-semibold text-[#FFFFFF]/90 transition-opacity hover:opacity-80'
      >
        &larr; Settings
      </button>
      <p className='shrink-0 text-[18px] font-semibold text-white' style={{ marginBottom: 12 }}>Notifications</p>
      <div className='hr-scroll min-h-0 flex-1 overflow-y-auto'>
        {loading && items.length === 0 ? (
          <p className='text-xs text-[#FFFFFF]/40'>Loading…</p>
        ) : items.length === 0 ? (
          <p className='text-xs text-[#FFFFFF]/40'>No notifications yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            {items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                expanded={expandedId === item.id}
                showUnreadDot={showUnreadDot(item.id, item.read)}
                onClick={() => handleRowClick(item.id, item.read)}
              />
            ))}
            {nextCursor && (
              <button
                type='button'
                onClick={() => void coordinator.loadMore()}
                className='mt-2 self-center border-none bg-transparent p-0 text-xs text-[#FFFFFF]/70 underline'
              >
                Load more
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
